import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  FormSelect,
  FormSelectOption,
  Label,
  SearchInput,
  ToggleGroup,
  ToggleGroupItem,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
} from '@patternfly/react-core';
import { ActionsColumn, type IAction, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationCircleIcon, ListIcon, PlusCircleIcon, SearchIcon, SyncAltIcon, TagIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { type AdminSplaLicenseFilters, useAdminSplaLicenses } from '../../../hooks/useAdminSplaCatalog';
import { formatUnitPrice } from '../../../utils/money';
import type { SplaAgreementType, SplaLicense } from '../../../types/models/Spla';
import type { ApiResponseSplaLicense, ApiResponseSplaLicenseRetired } from '../../../types/api/ApiResponseSpla';
import SplaLicenseModal from './SplaLicenseModal';
import SplaCategoriesModal from './SplaCategoriesModal';
import SplaLicensePriceModal from './SplaLicensePriceModal';
import SplaRetireLicenseModal from './SplaRetireLicenseModal';

const COLUMN_COUNT = 8;

// A flattened display row: either a category group header or a license, interleaved by walking the
// backend's distributor-sheet order (category section → price list → row order — never re-sorted
// client-side). The ProductsAndPrices DisplayRow pattern.
type DisplayRow =
  | { kind: 'group'; categoryId: number; categoryName: string; count: number }
  | { kind: 'license'; license: SplaLicense };

interface SplaCatalogTabProps {
  /** Defers every admin fetch until the page's staff check has passed. */
  isStaff: boolean;
}

/**
 * The SPLA license catalog — the mirror of the distributor's (Econocom) reporting sheet: category
 * sections with license rows, Corporate and Academic price lists. The `unit_price` column is the
 * DISTRIBUTOR price (what Ancalagon is charged) — a staff-only figure; customer sale prices live in
 * the per-license pricing modal.
 *
 * Retire = archive, never delete: past reports must still resolve retired part numbers, so the
 * DELETE verb archives and the UI says "Retire"/"Restore" — the word "Delete" appears nowhere for
 * licenses. Archived rows stay in the grid, flagged, by default (the management-grid contract).
 */
function SplaCatalogTab({ isStaff }: SplaCatalogTabProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [agreementType, setAgreementType] = useState<SplaAgreementType | null>(null);
  const [archivedFilter, setArchivedFilter] = useState<'0' | '1' | null>(null);
  const filters = useMemo<AdminSplaLicenseFilters>(
    () => ({ agreementType, archived: archivedFilter }),
    [agreementType, archivedFilter],
  );
  const { licenses, loading, error, refetch, applyLicense } = useAdminSplaLicenses(isStaff, filters);

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce the search so filtering doesn't run on every keystroke (CLAUDE.md: 400ms).
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchValue), 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  // Client-side filter on part number + item name + product family (the server owns the
  // agreement-type/archived narrowing; the text search stays local).
  const filteredLicenses = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) {
      return licenses;
    }
    return licenses.filter(
      (l) =>
        l.part_number.toLowerCase().includes(q) ||
        l.item_name.toLowerCase().includes(q) ||
        l.product_family.toLowerCase().includes(q),
    );
  }, [licenses, debouncedSearch]);

  // Emit a group header at each category change while WALKING the fetched order — the API already
  // returns distributor-sheet order and re-sorting is explicitly forbidden.
  const displayRows = useMemo<DisplayRow[]>(() => {
    const rows: DisplayRow[] = [];
    let currentCategoryId: number | null = null;
    filteredLicenses.forEach((license) => {
      if (license.category.id !== currentCategoryId) {
        currentCategoryId = license.category.id;
        const count = filteredLicenses.filter((l) => l.category.id === license.category.id).length;
        rows.push({
          kind: 'group',
          categoryId: license.category.id,
          categoryName: license.category.name ?? `Category #${license.category.id}`,
          count,
        });
      }
      rows.push({ kind: 'license', license });
    });
    return rows;
  }, [filteredLicenses]);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editLicense, setEditLicense] = useState<SplaLicense | null>(null);
  const [pricingLicense, setPricingLicense] = useState<SplaLicense | null>(null);
  const [retireLicense, setRetireLicense] = useState<SplaLicense | null>(null);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [restoringId, setRestoringId] = useState<number | null>(null);

  // A category rename changes the names embedded in license rows — refetch to re-group.
  const handleCategoriesChanged = useCallback(() => {
    void refetch();
  }, [refetch]);

  const handleSaved = useCallback(
    (license: SplaLicense, isCreate: boolean) => {
      // A moved row's sheet position is backend-decided: patching it in place would leave it under
      // the wrong category run and duplicate the walked group headers (duplicate React keys). The
      // pre-edit row is still in `editLicense` here (onSaved fires before onClose clears it).
      const positionChanged =
        editLicense === null ||
        license.category.id !== editLicense.category.id ||
        license.sort_order !== editLicense.sort_order ||
        license.agreement_type !== editLicense.agreement_type ||
        license.offering !== editLicense.offering;
      if (isCreate || positionChanged) {
        void refetch();
      } else {
        applyLicense(license);
      }
    },
    [refetch, applyLicense, editLicense],
  );

  // PATCH { archived: false } — the un-retire path. No confirm: restoring is non-destructive and
  // reversible (retire it again).
  const handleRestore = useCallback(
    async (license: SplaLicense) => {
      setRestoringId(license.id);
      try {
        const response = await apiFetch(`/admin/spla/licenses/${license.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ archived: false }),
        });
        // Branch on the JSend body.status, NEVER response.ok.
        const body = (await response.json().catch(() => null)) as ApiResponseSplaLicense | null;
        if (body?.status === 'success' && body.data?.license) {
          applyLicense(body.data.license);
          addNotification({
            title: 'License restored',
            srTitle: 'Success alert',
            description: `${license.part_number} (${license.agreement_type}) is back in the active catalog.`,
            variant: 'success',
          });
          setDrawerOpen(true);
        } else {
          addNotification({
            title: 'Could not restore the license',
            srTitle: 'Error alert',
            description: body?.message ?? 'Could not restore the license. Please try again.',
            variant: 'danger',
          });
          setDrawerOpen(true);
        }
      } catch (err) {
        addNotification({
          title: 'Could not restore the license',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      } finally {
        setRestoringId(null);
      }
    },
    [apiFetch, applyLicense, addNotification, setDrawerOpen],
  );

  // DELETE /admin/spla/licenses/{id} — ARCHIVES the license (idempotent; `already_archived` on a
  // repeat). Called by the retire confirm modal.
  const handleRetire = useCallback(
    async (license: SplaLicense): Promise<boolean> => {
      try {
        const response = await apiFetch(`/admin/spla/licenses/${license.id}`, { method: 'DELETE' });
        const body = (await response.json().catch(() => null)) as ApiResponseSplaLicenseRetired | null;
        if (body?.status === 'success' && body.data?.archived === true) {
          if (body.data.license) {
            applyLicense(body.data.license);
          }
          addNotification({
            title: body.data.already_archived ? 'License was already retired' : 'License retired',
            srTitle: 'Success alert',
            description: `${license.part_number} (${license.agreement_type}) stays in the catalog, flagged as retired — past reports still resolve it.`,
            variant: 'success',
          });
          setDrawerOpen(true);
          return true;
        }
        addNotification({
          title: 'Could not retire the license',
          srTitle: 'Error alert',
          description: body?.message ?? 'Could not retire the license. Please try again.',
          variant: 'danger',
        });
        setDrawerOpen(true);
        return false;
      } catch (err) {
        addNotification({
          title: 'Could not retire the license',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
        return false;
      }
    },
    [apiFetch, applyLicense, addNotification, setDrawerOpen],
  );

  const rowActions = (license: SplaLicense): IAction[] => [
    {
      title: 'Modify',
      onClick: () => setEditLicense(license),
    },
    {
      title: 'Pricing…',
      onClick: () => setPricingLicense(license),
    },
    { isSeparator: true },
    license.archived
      ? {
          title: 'Restore',
          onClick: () => void handleRestore(license),
          isAriaDisabled: restoringId !== null,
        }
      : {
          title: 'Retire',
          isDanger: true,
          onClick: () => setRetireLicense(license),
        },
  ];

  const clearAllFilters = () => {
    setSearchValue('');
    setAgreementType(null);
    setArchivedFilter(null);
  };

  const hasActiveFilter = debouncedSearch.trim() !== '' || agreementType !== null || archivedFilter !== null;

  const renderContent = () => {
    // Full-page spinner only on the genuine initial load; a manual Refresh keeps the table visible
    // and only the toolbar button spins.
    if (loading && licenses.length === 0) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load the license catalog"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (licenses.length === 0 && !hasActiveFilter) {
      return (
        <EmptyState titleText="No licenses yet" icon={ListIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>The SPLA catalog is empty.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsCreateOpen(true)}>
                Create license
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (filteredLicenses.length === 0) {
      return (
        <EmptyState titleText="No licenses match your filters" icon={SearchIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            Try a different search term or price list, or clear the filters to see the whole catalog.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={clearAllFilters}>
                Clear filters
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <Table aria-label="SPLA license catalog" variant="compact">
        <Thead>
          <Tr>
            <Th>Part number</Th>
            <Th>Item name</Th>
            <Th>Product family</Th>
            <Th>Agreement</Th>
            <Th>Offering</Th>
            <Th>Distributor price</Th>
            <Th>Remarks</Th>
            <Th screenReaderText="Row actions" />
          </Tr>
        </Thead>
        <Tbody>
          {displayRows.map((row) =>
            row.kind === 'group' ? (
              <Tr key={`group-${row.categoryId}`} className="ancalagon-product-group-header">
                <Td colSpan={COLUMN_COUNT}>
                  {row.categoryName}{' '}
                  <Content component="small">
                    ({row.count} {row.count === 1 ? 'license' : 'licenses'})
                  </Content>
                </Td>
              </Tr>
            ) : (
              <Tr key={row.license.id}>
                <Td dataLabel="Part number">
                  {row.license.part_number}
                  {row.license.archived && (
                    <>
                      {' '}
                      <Label color="grey" isCompact>
                        Retired
                      </Label>
                    </>
                  )}
                </Td>
                <Td dataLabel="Item name">
                  <Content component="small">{row.license.item_name}</Content>
                </Td>
                <Td dataLabel="Product family">{row.license.product_family}</Td>
                <Td dataLabel="Agreement">{row.license.agreement_type}</Td>
                <Td dataLabel="Offering">
                  {/* Corporate rows are always "Standard" — only the Academic split is meaningful. */}
                  {row.license.agreement_type === 'Academic' ? (
                    row.license.offering
                  ) : (
                    <Content component="small">—</Content>
                  )}
                </Td>
                <Td dataLabel="Distributor price">{formatUnitPrice(row.license.unit_price)}</Td>
                <Td dataLabel="Remarks">
                  {row.license.remarks !== null ? (
                    <Content component="small">{row.license.remarks}</Content>
                  ) : (
                    ''
                  )}
                </Td>
                <Td isActionCell>
                  <ActionsColumn items={rowActions(row.license)} />
                </Td>
              </Tr>
            ),
          )}
        </Tbody>
      </Table>
    );
  };

  return (
    <>
      <Toolbar clearAllFilters={clearAllFilters}>
        <ToolbarContent>
          <ToolbarItem>
            <SearchInput
              placeholder="Search by part number, item name or product family..."
              value={searchValue}
              onChange={(_event, value) => setSearchValue(value)}
              onClear={() => setSearchValue('')}
            />
          </ToolbarItem>
          <ToolbarItem>
            <ToggleGroup aria-label="Filter by price list">
              <ToggleGroupItem
                text="All"
                isSelected={agreementType === null}
                onChange={() => setAgreementType(null)}
              />
              <ToggleGroupItem
                text="Corporate"
                isSelected={agreementType === 'Corporate'}
                onChange={() => setAgreementType('Corporate')}
              />
              <ToggleGroupItem
                text="Academic"
                isSelected={agreementType === 'Academic'}
                onChange={() => setAgreementType('Academic')}
              />
            </ToggleGroup>
          </ToolbarItem>
          <ToolbarItem>
            <FormSelect
              value={archivedFilter ?? 'all'}
              onChange={(_event, value) => setArchivedFilter(value === 'all' ? null : (value as '0' | '1'))}
              aria-label="Filter by retired state"
            >
              <FormSelectOption value="all" label="Active and retired" />
              <FormSelectOption value="0" label="Active only" />
              <FormSelectOption value="1" label="Retired only" />
            </FormSelect>
          </ToolbarItem>
          <ToolbarGroup align={{ default: 'alignEnd' }}>
            <ToolbarItem>
              <Button
                variant="primary"
                icon={<PlusCircleIcon />}
                onClick={() => setIsCreateOpen(true)}
                isDisabled={loading && licenses.length === 0}
              >
                Create license
              </Button>
            </ToolbarItem>
            <ToolbarItem>
              <Button variant="secondary" icon={<TagIcon />} onClick={() => setIsCategoriesOpen(true)}>
                Manage categories
              </Button>
            </ToolbarItem>
            <ToolbarItem>
              <Button
                variant="secondary"
                icon={<SyncAltIcon />}
                onClick={() => void refetch()}
                isLoading={loading}
                isDisabled={loading}
              >
                Refresh
              </Button>
            </ToolbarItem>
          </ToolbarGroup>
        </ToolbarContent>
      </Toolbar>
      {renderContent()}
      {isCreateOpen && (
        <SplaLicenseModal isOpen license={null} onClose={() => setIsCreateOpen(false)} onSaved={handleSaved} />
      )}
      {editLicense && (
        <SplaLicenseModal
          key={editLicense.id}
          isOpen
          license={editLicense}
          onClose={() => setEditLicense(null)}
          onSaved={handleSaved}
        />
      )}
      {pricingLicense && (
        <SplaLicensePriceModal
          key={pricingLicense.id}
          isOpen
          license={pricingLicense}
          onClose={() => setPricingLicense(null)}
        />
      )}
      {retireLicense && (
        <SplaRetireLicenseModal
          license={retireLicense}
          onClose={() => setRetireLicense(null)}
          onRetire={handleRetire}
        />
      )}
      {isCategoriesOpen && (
        <SplaCategoriesModal isOpen onClose={() => setIsCategoriesOpen(false)} onChanged={handleCategoriesChanged} />
      )}
    </>
  );
}

export default SplaCatalogTab;
